import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { TEAM, VIEW_LABELS, isUserKey, type ThreadListItem } from '@mail/shared';
import { api } from '../lib/api.ts';
import { fmtWhen, groupOf, initials, participantsText } from '../lib/format.ts';
import {
  listKeyFor,
  loadList,
  loadMore,
  patchThread,
  removeThread,
  setQuery,
  setSideOpen,
  setSort,
  setView,
  useStore,
} from '../lib/store.ts';
import { IC } from './Icons.tsx';
import { toast } from './Toast.tsx';

// Schnellaktionen an einer Mail (mobil: Wischen und ⋯-Menü)
export type Aktion = 'done' | 'later' | 'pin' | 'mine' | 'unread';

const GROUP_ORDER = ['Heute', 'Gestern', 'Diese Woche'];

export function ThreadRow({
  t,
  selected,
  onOpen,
  color,
  own,
  onAktion,
  onMenu,
}: {
  t: ThreadListItem;
  selected: boolean;
  onOpen: () => void;
  color: string;
  own: Set<string>;
  onAktion?: (art: Aktion) => void;
  onMenu?: () => void;
}) {
  const who = participantsText(t.participants, own) || t.lastFrom?.address || '—';
  const as = t.assignedTo && isUserKey(t.assignedTo) ? TEAM[t.assignedTo] : null;
  // Wischen (Touch): nach links = erledigt, nach rechts = morgen früh wieder vorlegen
  const touch = useRef<{ x: number; y: number; dx: number; aktiv: boolean } | null>(null);
  const [dx, setDx] = useState(0);
  const swipe = dx <= -60 ? 'done' : dx >= 60 ? 'later' : '';
  return (
    <button
      className={`it ${t.unreadCount ? '' : 'read'} ${selected ? 'sel' : ''}${swipe ? ' swipe-' + swipe : ''}`}
      onClick={onOpen}
      data-id={t.id}
      // Ziehen auf „Junk“ / „Info & System“ / „Posteingang“ in der Seitenleiste sortiert um
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/x-mm-thread', String(t.id));
        e.dataTransfer.effectAllowed = 'move';
      }}
      style={
        dx
          ? { transform: `translateX(${Math.max(-110, Math.min(110, dx))}px)`, transition: 'none' }
          : undefined
      }
      onTouchStart={(e) => {
        if (!onAktion) return;
        const p = e.touches[0]!;
        touch.current = { x: p.clientX, y: p.clientY, dx: 0, aktiv: false };
      }}
      onTouchMove={(e) => {
        const st = touch.current;
        if (!st) return;
        const p = e.touches[0]!;
        const ddx = p.clientX - st.x;
        const ddy = p.clientY - st.y;
        if (!st.aktiv && Math.abs(ddx) > 14 && Math.abs(ddx) > Math.abs(ddy) * 1.5) st.aktiv = true;
        if (st.aktiv) {
          st.dx = ddx;
          setDx(ddx);
        }
      }}
      onTouchEnd={() => {
        const st = touch.current;
        touch.current = null;
        setDx(0);
        if (!st?.aktiv) return;
        if (st.dx <= -80) onAktion?.('done');
        else if (st.dx >= 80) onAktion?.('later');
      }}
    >
      {t.unreadCount > 0 && <span className="unread-dot" aria-label="ungelesen" />}
      {onMenu && (
        <span
          className="it-more"
          role="button"
          tabIndex={0}
          aria-label="Aktionen"
          onClick={(e) => {
            e.stopPropagation();
            onMenu();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onMenu();
            }
          }}
        >
          ⋯
        </span>
      )}
      <div className="av" style={{ background: `var(--mb-${color})` }} title={who}>
        {initials(t.lastFrom ?? t.participants[0])}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="r1">
          <span className="who">{who}</span>
          {t.pinned && <span title="angeheftet">📌</span>}
          <span className="when">{fmtWhen(t.lastDate)}</span>
        </div>
        <div className="subj">{t.subject || '(kein Betreff)'}</div>
        <div
          className="prev"
          {...(t.headline
            ? { dangerouslySetInnerHTML: { __html: t.headline } }
            : { children: t.snippet })}
        />
        <div className="tags">
          {t.messageCount > 1 && <span className="n">{t.messageCount}</span>}
          {t.answered && (
            <span className="muted" title="beantwortet" style={{ display: 'inline-flex' }}>
              {IC.reply}
            </span>
          )}
          {t.hasAttachments && (
            <span className="muted" title="Anhang" style={{ display: 'inline-flex' }}>
              {IC.clip}
            </span>
          )}
          {t.crmInstitutionName && <span className="pill einrichtung">{t.crmInstitutionName}</span>}
          {t.status === 'later' && t.snoozeUntil && (
            <span className="pill warn">⏰ {fmtWhen(t.snoozeUntil)}</span>
          )}
          {as && (
            <span
              className="mini-av"
              style={{ background: as.color }}
              title={`zugewiesen: ${as.name}`}
            >
              {as.name[0]}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export function ThreadList() {
  const nav = useNavigate();
  const [sheet, setSheet] = useState<ThreadListItem | null>(null);
  const {
    threads: geladen,
    view,
    mailbox,
    mailboxes,
    counts,
    selected,
    q,
    loading,
    sort,
    listError,
    listKey,
    listEnde,
    me,
  } = useStore((s) => ({
    sort: s.sort,
    threads: s.threads,
    listKey: s.listKey,
    listEnde: s.listEnde,
    me: s.me,
    view: s.view,
    mailbox: s.mailbox,
    mailboxes: s.mailboxes,
    counts: s.counts,
    selected: s.selected,
    q: s.q,
    listError: s.listError,
    loading: s.loading,
  }));
  // Nie Einträge einer anderen Ansicht oder Suche zeigen – etwa alte Suchtreffer, wenn das Nachladen
  // nach dem Leeren der Suche oder beim Ordnerwechsel scheiterte (API kurz weg)
  const passt = listKey === listKeyFor(view, mailbox, q);
  const threads = passt ? geladen : [];
  const own = useMemo(() => new Set(mailboxes.map((m) => m.address.toLowerCase())), [mailboxes]);
  const color = (id: number) => mailboxes.find((m) => m.id === id)?.color ?? 'green';
  const title = mailbox
    ? `${view === 'all' ? 'Archiv · ' : ''}${mailboxes.find((m) => m.id === mailbox)?.address ?? ''}`
    : `${VIEW_LABELS[view]} · alle Konten`;

  // Schnellaktion aus der Liste (mobil: Wischen oder ⋯), mit Rückgängig wo es etwas zurückzuholen gibt
  const aktion = async (t: ThreadListItem, art: Aktion) => {
    setSheet(null);
    try {
      if (art === 'done') {
        await api.done(t.id);
        removeThread(t.id);
        toast('Erledigt ✓', { undo: () => api.reopen(t.id).then(() => void loadList()) });
      } else if (art === 'later') {
        await api.later(t.id, 'tomorrow');
        removeThread(t.id);
        toast('Zurückgestellt: morgen früh', {
          undo: () => api.reopen(t.id).then(() => void loadList()),
        });
      } else if (art === 'pin') {
        await api.pin(t.id, !t.pinned);
        patchThread(t.id, { pinned: !t.pinned });
      } else if (art === 'mine') {
        const ziel = t.assignedTo === me?.key ? null : (me?.key ?? null);
        await api.assign(t.id, ziel);
        patchThread(t.id, { assignedTo: ziel });
      } else if (art === 'unread') {
        await api.seen(t.id, !t.unreadCount ? false : true);
        patchThread(t.id, { unreadCount: t.unreadCount ? 0 : 1 });
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Aktion fehlgeschlagen');
    }
  };

  const pinned = threads.filter((t) => t.pinned && view !== 'pinned');
  const rest = view === 'pinned' ? threads : threads.filter((t) => !t.pinned);
  const groups: Array<[string, ThreadListItem[]]> = [];
  for (const t of rest) {
    const g = groupOf(view === 'done' ? t.statusAt : t.lastDate);
    const last = groups[groups.length - 1];
    if (last && last[0] === g) last[1].push(t);
    else groups.push([g, [t]]);
  }
  groups.sort((a, b) => {
    const ia = GROUP_ORDER.indexOf(a[0]);
    const ib = GROUP_ORDER.indexOf(b[0]);
    if (ia >= 0 || ib >= 0) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    return 0;
  });

  const sorted = (counts?.sorted.info ?? 0) + (counts?.sorted.junk ?? 0);
  const done = counts?.doneToday ?? 0;
  const open = counts?.open ?? 0;
  const pct = done + open ? Math.round((done / (done + open)) * 100) : 100;
  const NOTES: Partial<Record<string, string>> = {
    waiting:
      'Du hast geschrieben, die andere Seite noch nicht. Nach 5 Tagen (je Thread änderbar) holt Bob den Thread mit einem Nachfass-Entwurf zurück in den Posteingang.',
    done: 'Beantwortet oder als erledigt markiert. Schreibt jemand zurück, landet der Thread automatisch wieder im Posteingang.',
    later: 'Zurückgestellt. Die Mail kommt zum gewählten Zeitpunkt wieder in den Posteingang.',
    info: 'Newsletter, Systemmeldungen und Benachrichtigungen. Keine Antwort nötig, deshalb ohne Bob-Entwurf.',
    junk: 'Werbung und Phishing-Verdacht. Wird nach 30 Tagen gelöscht. Links darin sind deaktiviert.',
  };

  return (
    <section className="list" aria-label="Mails">
      <div className="list-h">
        <button
          className="btn ghost burger"
          onClick={() => setSideOpen(true)}
          aria-label="Postfächer zeigen"
        >
          ☰
        </button>
        <h1>{title}</h1>
        <span className="pill mut">
          {loading ? '…' : `${threads.length} ${threads.length === 1 ? 'Thread' : 'Threads'}`}
        </span>
      </div>
      <label className="search">
        <span aria-hidden="true">⌕</span>
        <input
          type="search"
          placeholder="Suchen: Name, Schule, Betreff …"
          value={q}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // Escape leert das Feld – Chrome löscht bei type=search nativ, ohne immer ein input-Ereignis zu senden
            if (e.key === 'Escape') {
              e.preventDefault();
              setQuery('');
            }
          }}
          id="listSearch"
        />
      </label>
      <div className="items">
        {view === 'inbox' && !q && (
          <div className="zero">
            <div className="zero-t">
              <b>Heute erledigt: {done}</b>
              <span>noch {open} offen</span>
            </div>
            <div className="bar">
              <i style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {view === 'inbox' && !q && sorted > 0 && (
          <button
            className="sorted"
            onClick={() => setView(counts?.sorted.info ? 'info' : 'junk', mailbox)}
          >
            <span>🧹 Bob hat {sorted} Mails aussortiert</span>
            <span className="sorted-d">
              {counts?.sorted.info ?? 0} Info · {counts?.sorted.junk ?? 0} Junk · ansehen
            </span>
          </button>
        )}
        {NOTES[view] && !q && <p className="sorted-note">{NOTES[view]}</p>}
        {listError && (
          <p className="sorted-note" role="status">
            Liste konnte nicht geladen werden – wird gleich erneut versucht.
          </p>
        )}
        {!passt && !listError && !q && <p className="sorted-note">Lädt …</p>}
        {q && loading && <p className="sorted-note">Suche läuft …</p>}
        {q && !loading && !threads.length && (
          <p className="sorted-note">
            Nichts gefunden für „{q}“ – gesucht in allen Postfächern, auch Erledigtes.
          </p>
        )}
        {view === 'inbox' && !q && !loading && !threads.length && (
          <div className="zero-empty">
            <img className="bob-leer" src="/bob-figur.png" alt="" />
            <b>Posteingang leer.</b>
            <span>
              Alles beantwortet oder erledigt. Neue Antworten auf erledigte Mails tauchen hier
              automatisch wieder auf.
            </span>
          </div>
        )}
        {pinned.length > 0 && (
          <>
            <div className="grp">Angeheftet</div>
            {pinned.map((t) => (
              <ThreadRow
                key={t.id}
                t={t}
                selected={selected === t.id}
                color={color(t.mailboxId)}
                own={own}
                onOpen={() => void nav(`/t/${t.id}`)}
                onAktion={(art) => void aktion(t, art)}
                onMenu={() => setSheet(t)}
              />
            ))}
          </>
        )}
        {groups.map(([g, list]) => (
          <div key={g}>
            <div className="grp">{g}</div>
            {list.map((t) => (
              <ThreadRow
                key={t.id}
                t={t}
                selected={selected === t.id}
                color={color(t.mailboxId)}
                own={own}
                onOpen={() => void nav(`/t/${t.id}`)}
                onAktion={(art) => void aktion(t, art)}
                onMenu={() => setSheet(t)}
              />
            ))}
          </div>
        ))}
        {!loading && !threads.length && view !== 'inbox' && (
          <p className="empty" style={{ padding: '20px 14px' }}>
            Hier ist gerade nichts.
          </p>
        )}
        {!listEnde && threads.length > 0 && (
          <button className="btn mehr" onClick={() => void loadMore()} disabled={loading}>
            {loading ? 'Lädt …' : 'Ältere laden'}
          </button>
        )}
      </div>
      {sheet && (
        <div className="sheet-bg" onClick={() => setSheet(null)}>
          <div className="sheet" role="menu" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-t">{sheet.subject || '(kein Betreff)'}</div>
            <button onClick={() => void aktion(sheet, 'done')}>✓ Erledigt</button>
            <button onClick={() => void aktion(sheet, 'later')}>⏰ Später (morgen früh)</button>
            <button onClick={() => void aktion(sheet, 'pin')}>
              📌 {sheet.pinned ? 'Lösen' : 'Anheften'}
            </button>
            <button onClick={() => void aktion(sheet, 'mine')}>
              👤 {sheet.assignedTo === me?.key ? 'Zuweisung aufheben' : 'Mir zuweisen'}
            </button>
            <button onClick={() => void aktion(sheet, 'unread')}>
              {sheet.unreadCount ? 'Als gelesen' : 'Als ungelesen'}
            </button>
            <button className="sheet-x" onClick={() => setSheet(null)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
